import { useState, useContext, useEffect } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import DeleteIcon from "@mui/icons-material/Delete"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd"
import ListAltIcon from "@mui/icons-material/ListAlt"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const accordionStyles = {
  mb: 2,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "8px !important",
  boxShadow: "none",
  "&:before": { display: "none" },
}

const AsignarCartera = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [formData, setFormData] = useState({ usrasignado: "", comentario: "" })
  const [cuotas, setCuotas] = useState([])
  const [totalAsignar, setTotalAsignar] = useState(0)

  useEffect(() => {
    if (location.state?.cuotasSeleccionadas) {
      setCuotas(location.state.cuotasSeleccionadas)
    } else {
      navigate("/home/dashboard/AsignaciondeCartera")
    }
  }, [location, navigate])

  useEffect(() => {
    const total = cuotas.reduce((acc, curr) => acc + (parseFloat(curr.facsaldo) || 0), 0)
    setTotalAsignar(total)
  }, [cuotas])

  // Lógica de extracción idéntica al ejemplo CrearContraCliDF
  const { data: rawUsuariosData, isLoading: isLoadingUsuarios } = useQuery({
    queryKey: ["getUsuariosAsignables"],
    queryFn: async () => {
      try {
        const response = await api.post("/CreacionUsuarios/getAllUsuarios", {
          page: 1,
          perPage: 1000,
          filters: { usrstatus: "A", usrflagoficre: -1 },
        })
        return response?.data?.data?.data || response?.data?.data || response?.data || []
      } catch (error) {
        return []
      }
    },
    refetchOnWindowFocus: false,
  })

  // Mapeo seguro a la estructura requerida por el Autocomplete
  const listaUsuarios = (Array.isArray(rawUsuariosData) ? rawUsuariosData : []).map((u) => ({
    id: u.usrcodigo,
    label: `${u.usrcodigo} - ${u.usrnombre || ""}`,
  }))

  const { mutateAsync: SaveAsignacion, isPending: isSaving } = useMutation({
    queryKey: ["isCreatingAsignacionCartera"],
    fn: async (dataPayload) => (await api.post("/AsignaciondeCartera/createAsignaciondeCartera", dataPayload)).data,
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleRemoveCuota = (idx) => {
    const nCuotas = [...cuotas]
    nCuotas.splice(idx, 1)
    setCuotas(nCuotas)
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!formData.usrasignado) return showWarning("Debe seleccionar un recaudador para la asignación.")
    if (cuotas.length === 0) return showWarning("La lista de cartera está vacía. Seleccione cuotas para asignar.")

    const payload = {
      usrasignado: formData.usrasignado,
      comentario: formData.comentario,
      cuotas: cuotas.map((c) => ({ facnumfac: c.facnumfac, facsecuen: c.facsecuen })),
    }
    await SaveAsignacion(payload)
  }

  const action = selectedMenuInfo?.data?.barraAcciones?.find(
    (a) => a.acccaption === "GRABAR" || a.acccaption === "ASIGNAR",
  )

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2 }}>
          {action && (
            <Tooltip title={action.acccaption}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSaving || isLoadingUsuarios}
                sx={{ border: "1px solid #ddd", bgcolor: "white", borderRadius: 1 }}
              >
                {getIconComponent(action.accnameicono, action.acctipoico)}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <div style={{ display: "flex", justifyContent: "center", mb: "30px", fontSize: "25px" }}>
          <b>Ejecutar Asignación de Cartera</b>
        </div>

        <CustomBackdrop isLoading={isSaving || isLoadingUsuarios} />

        <Box sx={StyledRoot} component="form" onSubmit={handleSubmit}>
          <Accordion defaultExpanded sx={accordionStyles}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AssignmentIndIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  PARÁMETROS DE ASIGNACIÓN
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={listaUsuarios}
                    getOptionLabel={(o) => o.label || ""}
                    value={listaUsuarios.find((u) => u.id === formData.usrasignado) || null}
                    onChange={(e, v) => setFormData((prev) => ({ ...prev, usrasignado: v ? v.id : "" }))}
                    renderInput={(p) => (
                      <TextField
                        {...p}
                        label="Seleccione el Recaudador Asignado *"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monto Total a Asignar ($)"
                    value={totalAsignar.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    sx={{ bgcolor: "#f9f9f9", input: { fontWeight: "bold", color: "#b71c1c", textAlign: "right" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    fullWidth
                    label="Instrucciones o Comentario (Opcional)"
                    value={formData.comentario}
                    onChange={(e) => setFormData((prev) => ({ ...prev, comentario: e.target.value.toUpperCase() }))}
                    placeholder="Ej: GESTIONAR URGENTE ESTA SEMANA"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded sx={accordionStyles}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ListAltIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  DETALLE DE LA CARTERA ({cuotas.length} Cuotas)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell align="center">
                        <b>#</b>
                      </TableCell>
                      <TableCell align="center">
                        <b>Factura</b>
                      </TableCell>
                      <TableCell align="center">
                        <b>Cuota</b>
                      </TableCell>
                      <TableCell>
                        <b>Cliente</b>
                      </TableCell>
                      <TableCell align="center">
                        <b>Vencimiento</b>
                      </TableCell>
                      <TableCell align="right">
                        <b>Saldo</b>
                      </TableCell>
                      <TableCell align="center">
                        <b>Quitar</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cuotas?.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell align="center">{idx + 1}</TableCell>
                        <TableCell align="center">{row.facnumfac}</TableCell>
                        <TableCell align="center">{row.facsecuen}</TableCell>
                        <TableCell>{row.clinombre}</TableCell>
                        <TableCell align="center">{row.facfecven}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          ${Number(row.facsaldo).toFixed(2)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="error" onClick={() => handleRemoveCuota(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default AsignarCartera
